import React from 'react';
import en from './locales/en';
import ko from './locales/ko';
import ru from './locales/ru';
import zh from './locales/zh';
import vi from './locales/vi';
import th from './locales/th';
import ja from './locales/ja';
import it from './locales/it';
import fr from './locales/fr';
import hi from './locales/hi';
import ur from './locales/ur';
import es from './locales/es';
import domainSupplement from './locales/domainSupplement';
import legacyCompletion from './locales/legacyCompletion';
import uiCompletion from './locales/uiCompletion';
import feeDefaultTranslations from './locales/feeDefaultTranslations';
import phase1Completion from './locales/phase1Completion';
import phase2Completion from './locales/phase2Completion';
import phase34Completion from './locales/phase34Completion';
import hardcodedCleanup from './locales/hardcodedCleanup';
import financeTranslations from './locales/financeTranslations';

export const DEFAULT_ADMIN_LANGUAGE = 'en';
export const ADMIN_LANGUAGE_STORAGE_KEY = 'admin-ui-language';

export const ADMIN_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ko', label: '한국어' },
  { value: 'ru', label: 'Русский' },
  { value: 'zh', label: '中文' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'th', label: 'ไทย' },
  { value: 'ja', label: '日本語' },
  { value: 'it', label: 'Italiano' },
  { value: 'fr', label: 'Français' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'ur', label: 'اردو' },
  { value: 'es', label: 'Español' },
];

const baseResources = { en, ko, ru, zh, vi, th, ja, it, fr, hi, ur, es };
const resources = Object.fromEntries(
  Object.entries(baseResources).map(([lang, table]) => [
    lang,
    { ...table, ...financeTranslations.en, ...(financeTranslations[lang] || {}), ...(domainSupplement[lang] || {}), ...(uiCompletion[lang] || {}), ...(legacyCompletion[lang] || {}), ...(feeDefaultTranslations[lang] || {}), ...(phase1Completion[lang] || {}), ...(phase2Completion[lang] || {}), ...(phase34Completion[lang] || {}), ...(hardcodedCleanup[lang] || {}) },
  ])
);

// Korean keys are the canonical source strings. Mirror every final English key into
// the Korean table so ko/en always expose the exact same key set.
resources.ko = Object.fromEntries(
  Object.keys(resources.en || {}).map((key) => [key, resources.ko?.[key] ?? key])
);

export function normalizeLanguage(lang) {
  return ADMIN_LANGUAGE_OPTIONS.some((item) => item.value === lang) ? lang : DEFAULT_ADMIN_LANGUAGE;
}

export function getAdminLanguage() {
  try {
    return normalizeLanguage(localStorage.getItem(ADMIN_LANGUAGE_STORAGE_KEY) || DEFAULT_ADMIN_LANGUAGE);
  } catch (_) {
    return DEFAULT_ADMIN_LANGUAGE;
  }
}

const ADMIN_LOCALE_MAP = {
  en: 'en-US',
  ko: 'ko-KR',
  ru: 'ru-RU',
  zh: 'zh-CN',
  vi: 'vi-VN',
  th: 'th-TH',
  ja: 'ja-JP',
  it: 'it-IT',
  fr: 'fr-FR',
  hi: 'hi-IN',
  ur: 'ur-PK',
  es: 'es-ES',
};

export function getAdminLocale() {
  return ADMIN_LOCALE_MAP[getAdminLanguage()] || ADMIN_LOCALE_MAP[DEFAULT_ADMIN_LANGUAGE];
}

export function setAdminLanguage(lang) {
  const next = normalizeLanguage(lang);
  try {
    localStorage.setItem(ADMIN_LANGUAGE_STORAGE_KEY, next);
  } catch (_) {
    // ignore storage failures
  }
  return next;
}

function interpolate(template, params) {
  if (!params || typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match
  ));
}

export function t(key, params) {
  const lang = getAdminLanguage();

  if (lang === 'ko') {
    const koTable = resources.ko || {};
    return interpolate(koTable[key] ?? key, params);
  }

  const table = resources[lang] || resources[DEFAULT_ADMIN_LANGUAGE] || {};
  const fallbackTable = resources[DEFAULT_ADMIN_LANGUAGE] || {};
  const template = table[key] ?? fallbackTable[key] ?? key;
  if (template === key && /[가-힣]/.test(String(key || ''))) {
    return interpolate('Translation unavailable', params);
  }
  return interpolate(template, params);
}

export function localizeServerMessage(message, fallbackKey = '서버 오류', params) {
  const raw = String(message ?? '').trim();
  const fallback = t(fallbackKey, params);
  if (!raw) return fallback;
  if (getAdminLanguage() === 'ko') return interpolate(raw, params);

  const translated = t(raw, params);
  if (translated !== raw) return translated;

  // Never leak backend Korean copy into a non-Korean admin UI.
  // English/already-localized backend messages can pass through unchanged.
  if (/[가-힣]/.test(raw)) return fallback;
  return interpolate(raw, params);
}

export function localizeApiError(error, fallbackKey = '서버 오류', params) {
  const raw =
    error?.response?.data?.message ||
    error?.response?.data?.cashier?.message ||
    error?.message ||
    '';
  return localizeServerMessage(raw, fallbackKey, params);
}

export function LanguageSelect({ className = '' }) {
  const [value, setValue] = React.useState(getAdminLanguage);

  const handleChange = (event) => {
    const next = setAdminLanguage(event.target.value);
    setValue(next);
    window.location.reload();
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      aria-label={t('언어')}
      className={[
        'px-2 py-1 rounded border border-gray-300 bg-white text-sm text-gray-700',
        className,
      ].filter(Boolean).join(' ')}
    >
      {ADMIN_LANGUAGE_OPTIONS.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
}
